<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import DatePicker from "vue2-datepicker";


// Fallback sample data for Delivery Unit -> Process Types
const SAMPLE_DU_PROCESS_TYPES = [];

export default {
    page: {
        title: "Track Summary"
    },
    components: {
        Layout,
        PageHeader,
        DatePicker
    },
    data() {
        return {
            title: "Track Summary",
            items: [
                {
                    text: "Dashboards",
                    href: "/"
                },
                {
                    text: "Track Summary",
                    active: true
                }
            ],
            // Main Tab: 'campus' | 'deliveryUnit'
            activeTab: "campus",
            // Sub-view button: 'all' | 'processTypes'
            subView: "all",
            // Display mode: 'table' | 'chart'
            displayMode: "table",

            // Date Range Filters (Default Jan 1, 2026 to Dec 31, 2026)
            from: new Date(new Date().getFullYear(), 0, 1),
            to: new Date(new Date().getFullYear(), 11, 31, 23, 59, 59),

            // Search & Pagination
            searchFilter: "",
            entityFilter: "", // Filter by specific Campus or DU in Process Types view
            perPage: 10,
            pageOptions: [10, 25, 50, 100],
            currentPage: 1,

            // Table Sorting
            sortKey: "totalTracks",
            sortDesc: true,

            // Loading State
            loading: false,

            // Chart Display Settings
            chartOrientation: "vertical", // 'vertical' | 'horizontal'
            chartStacked: false,
            chartKey: 1,

            // Raw Data Stores
            campuses: [],
            selectedCampusId: null,
            campusAllData: [],
            campusProcessTypesData: [],
            deliveryUnitAllData: [],
            deliveryUnitProcessTypesData: [],

            isUsingFallback: false
        };
    },
    computed: {
        tabIndex: {
            get() {
                return this.activeTab === "campus" ? 0 : 1;
            },
            set(val) {
                this.changeTab(val === 0 ? "campus" : "deliveryUnit");
            }
        },
        campusOptions() {
            if (this.campuses && this.campuses.length > 0) {
                return this.campuses;
            }
            if (this.campusAllData && this.campusAllData.length > 0) {
                return this.campusAllData.map(c => ({
                    id: c.campusId || c.id,
                    campusName: c.campusName || c.campus
                }));
            }
            return [];
        },
        // Current primary dataset depending on activeTab and subView
        currentRawList() {
            if (this.activeTab === "campus") {
                return this.subView === "all"
                    ? (this.campusAllData.length ? this.campusAllData : [])
                    : (this.campusProcessTypesData.length ? this.campusProcessTypesData : []);
            } else {
                return this.subView === "all"
                    ? (this.deliveryUnitAllData.length ? this.deliveryUnitAllData : [])
                    : (this.deliveryUnitProcessTypesData.length ? this.deliveryUnitProcessTypesData : []);
            }
        },

        // List of unique campuses or delivery units for dropdown filtering in By Process Types view
        entityFilterOptions() {
            if (this.subView !== "processTypes") return [];
            const set = new Set();
            const list = [];
            this.currentRawList.forEach(item => {
                const name = this.activeTab === "campus"
                    ? (item.campusName || item.campus || "Unknown Campus")
                    : (item.deliveryUnitName || item.deliveryUnit || "Unknown Delivery Unit");
                if (name && !set.has(name)) {
                    set.add(name);
                    list.push(name);
                }
            });
            return list.sort();
        },

        // Filtered and Sorted list
        filteredList() {
            let list = [...this.currentRawList];

            // Entity filter (dropdown for Process Types view)
            if (this.entityFilter && this.entityFilter.trim() !== "") {
                const ef = this.entityFilter.toUpperCase();
                list = list.filter(item => {
                    const name = this.activeTab === "campus"
                        ? (item.campusName || item.campus || "")
                        : (item.deliveryUnitName || item.deliveryUnit || "");
                    return name.toUpperCase() === ef;
                });
            }

            // Keyword text filter
            if (this.searchFilter && this.searchFilter.trim() !== "") {
                const q = this.searchFilter.toUpperCase().trim();
                list = list.filter(item => {
                    const campus = (item.campusName || item.campus || "").toUpperCase();
                    const du = (item.deliveryUnitName || item.deliveryUnit || "").toUpperCase();
                    const pt = (item.processTypeName || item.processType || item.processName || "").toUpperCase();
                    return campus.includes(q) || du.includes(q) || pt.includes(q);
                });
            }

            // Sorting
            const key = this.sortKey;
            const desc = this.sortDesc;
            list.sort((a, b) => {
                let valA = a[key];
                let valB = b[key];

                // Derived completion rate for sorting
                if (key === "completionRate") {
                    valA = a.totalTracks ? (a.completedTracks / a.totalTracks) : 0;
                    valB = b.totalTracks ? (b.completedTracks / b.totalTracks) : 0;
                }

                if (typeof valA === "string") {
                    return desc ? valB.localeCompare(valA) : valA.localeCompare(valB);
                }
                valA = Number(valA || 0);
                valB = Number(valB || 0);
                return desc ? valB - valA : valA - valB;
            });

            return list;
        },

        // Paginated list for table view
        paginatedList() {
            if (this.perPage === -1) return this.filteredList;
            const start = (this.currentPage - 1) * this.perPage;
            return this.filteredList.slice(start, start + this.perPage);
        },

        // Total rows count
        totalRows() {
            return this.filteredList.length;
        },

        // KPI Summary Totals computed on the current filtered dataset
        summaryKpi() {
            const list = this.filteredList;
            let totalTracks = 0;
            let pendingTracks = 0;
            let completedTracks = 0;

            list.forEach(item => {
                totalTracks += Number(item.totalTracks || 0);
                pendingTracks += Number(item.pendingTracks || 0);
                completedTracks += Number(item.completedTracks || 0);
            });

            const rate = totalTracks > 0 ? ((completedTracks / totalTracks) * 100).toFixed(1) : "0.0";

            return {
                totalTracks,
                pendingTracks,
                completedTracks,
                rate
            };
        },

        // ApexCharts Series Data
        chartSeries() {
            const list = this.filteredList.slice(0, 30); // Top 30 items for readability
            return [
                {
                    name: "Completed Tracks",
                    data: list.map(item => Number(item.completedTracks || 0))
                },
                {
                    name: "Pending Tracks",
                    data: list.map(item => Number(item.pendingTracks || 0))
                },
                {
                    name: "Total Tracks",
                    data: list.map(item => Number(item.totalTracks || 0))
                }
            ];
        },

        // ApexCharts Options Configuration
        chartOptions() {
            const isHorizontal = this.chartOrientation === "horizontal";
            const list = this.filteredList.slice(0, 30);

            const categories = list.map(item => {
                if (this.subView === "all") {
                    return this.activeTab === "campus"
                        ? (item.campusName || `Campus #${item.campusId}`)
                        : (item.deliveryUnitName || `DU #${item.deliveryUnitId}`);
                } else {
                    const pt = item.processTypeName || item.processType || "Process";
                    const entity = this.activeTab === "campus"
                        ? (item.campusName || "")
                        : (item.deliveryUnitName || "");
                    return entity ? `${pt} (${entity})` : pt;
                }
            });

            return {
                chart: {
                    type: "bar",
                    height: Math.max(480, isHorizontal ? categories.length * 32 : 480),
                    stacked: this.chartStacked,
                    toolbar: {
                        show: true,
                        tools: {
                            download: true,
                            selection: false,
                            zoom: false,
                            zoomin: false,
                            zoomout: false,
                            pan: false,
                            reset: false
                        }
                    }
                },
                plotOptions: {
                    bar: {
                        horizontal: isHorizontal,
                        columnWidth: "55%",
                        barHeight: "65%",
                        borderRadius: 4,
                        dataLabels: {
                            position: "top"
                        }
                    }
                },
                dataLabels: {
                    enabled: false
                },
                colors: ["#34c38f", "#f1b44c", "#556ee6"], // Completed, Pending, Total
                stroke: {
                    show: true,
                    width: 1,
                    colors: ["#fff"]
                },
                xaxis: {
                    categories: categories,
                    labels: {
                        rotate: isHorizontal ? 0 : -35,
                        rotateAlways: false,
                        trim: true,
                        maxHeight: 120,
                        style: {
                            fontSize: "11px",
                            fontWeight: 500
                        }
                    }
                },
                yaxis: {
                    title: {
                        text: isHorizontal ? "" : "Number of Tracks"
                    },
                    labels: {
                        formatter: val => Number(val).toLocaleString()
                    }
                },
                tooltip: {
                    shared: true,
                    intersect: false,
                    y: {
                        formatter: val => `${Number(val).toLocaleString()} tracks`
                    }
                },
                legend: {
                    position: "top",
                    horizontalAlign: "center"
                },
                grid: {
                    borderColor: "#f1f1f1"
                }
            };
        }
    },
    watch: {
        activeTab() {
            this.currentPage = 1;
            this.searchFilter = "";
            this.entityFilter = "";
            this.chartKey++;
            this.fetchCurrentData();
        },
        subView() {
            this.currentPage = 1;
            this.searchFilter = "";
            this.entityFilter = "";
            this.chartKey++;
            this.fetchCurrentData();
        },
        selectedCampusId(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.currentPage = 1;
                this.searchFilter = "";
                this.entityFilter = "";
                this.chartKey++;
                this.fetchCurrentData();
            }
        },
        displayMode() {
            this.chartKey++;
        },
        from(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.fetchCurrentData();
            }
        },
        to(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.fetchCurrentData();
            }
        }
    },
    methods: {
        // Main Tab Switcher: 'campus' | 'deliveryUnit'
        changeTab(tab) {
            if (this.activeTab !== tab) {
                this.activeTab = tab;
            }
        },

        // Sub-view Switcher: 'all' | 'processTypes'
        changeSubView(sub) {
            if (this.subView !== sub) {
                this.subView = sub;
            }
        },

        // Toggle Table Sorting
        toggleSort(key) {
            if (this.sortKey === key) {
                this.sortDesc = !this.sortDesc;
            } else {
                this.sortKey = key;
                this.sortDesc = true;
            }
        },

        // Toggle Bar Chart Orientation
        toggleOrientation() {
            this.chartOrientation = this.chartOrientation === "vertical" ? "horizontal" : "vertical";
            this.chartKey++;
        },

        // Toggle Bar Chart Stacked/Grouped
        toggleStacked() {
            this.chartStacked = !this.chartStacked;
            this.chartKey++;
        },

        // Preset Date Filters
        setThisYear() {
            const year = new Date().getFullYear();
            this.from = new Date(year, 0, 1);
            this.to = new Date(year, 11, 31, 23, 59, 59);
        },
        setLast30Days() {
            const to = new Date();
            const from = new Date();
            from.setDate(to.getDate() - 30);
            this.from = from;
            this.to = to;
        },
        setAllTime() {
            this.from = new Date(2020, 0, 1);
            this.to = new Date(2030, 11, 31, 23, 59, 59);
        },

        // Drill down from All view to Process Types view for a specific campus / DU
        drillDownToProcessTypes(item) {
            const name = this.activeTab === "campus"
                ? (item.campusName || item.campus)
                : (item.deliveryUnitName || item.deliveryUnit);
            if (this.activeTab === "campus" && (item.campusId || item.id)) {
                this.selectedCampusId = item.campusId || item.id;
            }
            this.subView = "processTypes";
            this.entityFilter = name || "";
        },
        async getCampuses() {
            try {
                const res = await this.$store.dispatch("campus/GetCampus");
                if (res && res.data && !res.data.error) {
                    this.campuses = res.data;
                    if (!this.selectedCampusId && this.campuses.length > 0) {
                        const defaultCampus = this.$store.state.data.selectedCampus;
                        const match = defaultCampus ? this.campuses.find(c => c.id === defaultCampus.id) : null;
                        this.selectedCampusId = match ? match.id : this.campuses[0].id;
                    }
                }
            } catch (err) {
                console.warn("Could not load campuses:", err);
            }
        },
        onCampusChange() {
            this.currentPage = 1;
            this.searchFilter = "";
            this.entityFilter = "";
            this.chartKey++;
            this.fetchCurrentData();
        },

        // Data normalizer for API responses that might be nested or flat
        normalizeProcessTypeData(data, type) {
            if (!Array.isArray(data)) return [];
            const normalized = [];
            data.forEach((item, idx) => {
                const nested = item.processTypes || item.childProcessTypes || item.items || item.processTypeSummaries;
                if (Array.isArray(nested) && nested.length > 0) {
                    nested.forEach((sub, sIdx) => {
                        normalized.push({
                            id: `${type}-${item.id || item.campusId || item.deliveryUnitId || idx}-${sub.id || sub.processTypeId || sIdx}`,
                            campusId: item.campusId || item.id,
                            campusName: item.campusName || item.campus || "",
                            deliveryUnitId: item.deliveryUnitId || item.id,
                            deliveryUnitName: item.deliveryUnitName || item.deliveryUnit || item.name || "",
                            processTypeId: sub.processTypeId || sub.id || sIdx,
                            processTypeName: sub.processTypeName || sub.processType || sub.name || "General Process",
                            totalTracks: Number(sub.totalTracks || 0),
                            pendingTracks: Number(sub.pendingTracks || 0),
                            completedTracks: Number(sub.completedTracks || 0)
                        });
                    });
                } else {
                    normalized.push({
                        id: item.id || idx,
                        campusId: item.campusId,
                        campusName: item.campusName || item.campus || "",
                        deliveryUnitId: item.deliveryUnitId,
                        deliveryUnitName: item.deliveryUnitName || item.deliveryUnit || item.name || "",
                        processTypeId: item.processTypeId,
                        processTypeName: item.processTypeName || item.processType || item.processName || "General Process",
                        totalTracks: Number(item.totalTracks || 0),
                        pendingTracks: Number(item.pendingTracks || 0),
                        completedTracks: Number(item.completedTracks || 0)
                    });
                }
            });
            return normalized;
        },

        // Unified fetcher based on current active tab and subview
        async fetchCurrentData() {
            this.loading = true;
            const cid = this.selectedCampusId || this.$store.state.data.selectedCampus?.id || 1;
            const payload = {
                dateFrom: this.getDate(this.from),
                dateTo: this.getDate(this.to),
                campusId: cid,
                removeLoading: true
            };

            try {
                if (this.activeTab === "campus") {
                    if (this.subView === "all") {
                        const { data: response } = await this.$store.dispatch("dtstrack/GetTrackSummaryGroupedByCampus", payload);
                        if (response && !response.error) {
                            const resList = Array.isArray(response) ? response : (response.data || []);
                            if (resList.length > 0) {
                                this.campusAllData = resList;
                                this.isUsingFallback = false;
                            }
                        } else if (response && response.error) {
                            this.showToast(response.errorMessage, "error");
                        }
                    } else {
                        const { data: response } = await this.$store.dispatch("dtstrack/GetTrackSummaryGroupedByCampusAndProcessType", payload);
                        if (response && !response.error) {
                            const resList = Array.isArray(response) ? response : (response.data || []);
                            if (resList.length > 0) {
                                this.campusProcessTypesData = this.normalizeProcessTypeData(resList, "campus");
                                this.isUsingFallback = false;
                            }
                        } else if (response && response.error) {
                            this.showToast(response.errorMessage, "error");
                        }
                    }
                } else {
                    // By Delivery Unit
                    if (this.subView === "all") {
                        const { data: response } = await this.$store.dispatch("dtstrack/GetTrackSummaryGroupedByDU", payload);
                        if (response && !response.error) {
                            const resList = Array.isArray(response) ? response : (response.data || []);
                            if (resList.length > 0) {
                                this.deliveryUnitAllData = resList;
                                this.isUsingFallback = false;
                            }
                        } else if (response && response.error) {
                            this.showToast(response.errorMessage, "error");
                        }
                    } else {
                        const { data: response } = await this.$store.dispatch("dtstrack/GetTrackSummaryGroupedByDUAndProcessType", payload);
                        if (response && !response.error) {
                            const resList = Array.isArray(response) ? response : (response.data || []);
                            if (resList.length > 0) {
                                this.deliveryUnitProcessTypesData = this.normalizeProcessTypeData(resList, "deliveryUnit");
                                this.isUsingFallback = false;
                            }
                        } else if (response && response.error) {
                            this.showToast(response.errorMessage, "error");
                        }
                    }
                }
            } catch (error) {
                // Fallback gracefully to default sample datasets if offline or unauthorized
                this.isUsingFallback = true;
                console.warn("API request error, displaying sample data:", error);
            } finally {
                this.loading = false;
                this.chartKey++;
            }
        },

        // Format Number with Commas
        formatNumber(num) {
            if (num === null || num === undefined) return "0";
            return Number(num).toLocaleString();
        },

        // Calculate completion percentage
        getCompletionPercentage(completed, total) {
            if (!total || total === 0) return 0;
            return Math.min(100, Math.round((completed / total) * 100));
        },

        // Export current filtered table view to CSV
        exportToCsv() {
            const list = this.filteredList;
            if (!list.length) {
                this.showToast("No data to export.", "warning");
                return;
            }

            let headers = [];
            let rows = [];

            if (this.subView === "all") {
                const entityHeader = this.activeTab === "campus" ? "Campus Name" : "Delivery Unit";
                headers = [entityHeader, "Total Tracks", "Pending Tracks", "Completed Tracks", "Completion Rate (%)"];
                rows = list.map(item => {
                    const name = this.activeTab === "campus"
                        ? (item.campusName || "")
                        : (item.deliveryUnitName || item.deliveryUnit || "");
                    const rate = item.totalTracks > 0 ? ((item.completedTracks / item.totalTracks) * 100).toFixed(1) : 0;
                    return [`"${name.replace(/"/g, '""')}"`, item.totalTracks, item.pendingTracks, item.completedTracks, `${rate}%`];
                });
            } else {
                const entityHeader = this.activeTab === "campus" ? "Campus" : "Delivery Unit";
                headers = [entityHeader, "Process Type", "Total Tracks", "Pending Tracks", "Completed Tracks", "Completion Rate (%)"];
                rows = list.map(item => {
                    const entity = this.activeTab === "campus"
                        ? (item.campusName || "")
                        : (item.deliveryUnitName || item.deliveryUnit || "");
                    const pt = item.processTypeName || item.processType || "";
                    const rate = item.totalTracks > 0 ? ((item.completedTracks / item.totalTracks) * 100).toFixed(1) : 0;
                    return [
                        `"${entity.replace(/"/g, '""')}"`,
                        `"${pt.replace(/"/g, '""')}"`,
                        item.totalTracks,
                        item.pendingTracks,
                        item.completedTracks,
                        `${rate}%`
                    ];
                });
            }

            const csvContent = "data:text/csv;charset=utf-8,"
                + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            const filename = `track_summary_${this.activeTab}_${this.subView}_${new Date().toISOString().slice(0, 10)}.csv`;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            this.showToast("CSV exported successfully!", "success");
        }
    },
    async created() {
        // Populate sample dataset initially so view renders instantly
        this.campusAllData = [];
        this.campusProcessTypesData = [];
        this.deliveryUnitAllData = [];
        this.deliveryUnitProcessTypesData = SAMPLE_DU_PROCESS_TYPES;

        // Fetch campuses and ensure selectedCampusId is set
        await this.getCampuses();
        if (!this.selectedCampusId) {
            this.selectedCampusId = this.$store.state.data.selectedCampus?.id || (this.campusOptions[0]?.id || 0);
        }
        // Fetch live data from backend
        this.fetchCurrentData();
    }
};
</script>

<template>
    <Layout @changeCampus="fetchCurrentData">
        <PageHeader :title="title" :items="items" />

        <!-- Top Filter & Date Range Toolbar -->
        <div class="card shadow-sm mb-4">
            <div class="card-body p-3">
                <div class="d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <!-- Left: Subtitle & Quick Info -->
                    <div class="d-flex align-items-center gap-2">
                        <div>
                            <h5 class="font-size-15 mb-0 text-dark font-weight-bold">Document Tracking Analytics</h5>
                            <p class="text-muted font-size-12 mb-0">Overview of document tracks across campuses and
                                delivery units</p>
                        </div>
                    </div>

                    <!-- Right: Date Range Pickers & Presets -->
                    <div class="d-flex flex-wrap align-items-center gap-2">

                        <div class="d-flex align-items-center gap-1">
                            <span class="text-muted font-size-12 fw-medium">From:</span>
                            <date-picker v-model="from" :first-day-of-week="1" lang="en" type="date"
                                placeholder="Start Date" class="date-picker-sm"></date-picker>
                        </div>

                        <div class="d-flex align-items-center gap-1">
                            <span class="text-muted font-size-12 fw-medium">To:</span>
                            <date-picker v-model="to" :first-day-of-week="1" lang="en" type="date"
                                placeholder="End Date" class="date-picker-sm"></date-picker>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- KPI Metric Summary Cards -->
        <div class="row mb-3">
            <!-- Total Tracks -->
            <div class="col-xl-3 col-md-6 mb-3">
                <div class="card card-hover-shadow mb-0 h-100 border-start border-4 border-primary">
                    <div class="card-body">
                        <div class="d-flex align-items-center justify-content-between">
                            <div>
                                <span class="text-muted text-uppercase fw-semibold font-size-12">Total Tracks</span>
                                <h3 class="my-1 text-primary fw-bold font-size-22">
                                    {{ formatNumber(summaryKpi.totalTracks) }}
                                </h3>
                                <span class="text-muted font-size-11">
                                    <i class="bx bx-layer text-primary me-1"></i> Recorded in system
                                </span>
                            </div>
                            <div class="avatar-sm">
                                <span class="avatar-title bg-soft-primary rounded-circle text-primary font-size-22">
                                    <i class="bx bx-file"></i>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Pending Tracks -->
            <div class="col-xl-3 col-md-6 mb-3">
                <div class="card card-hover-shadow mb-0 h-100 border-start border-4 border-warning">
                    <div class="card-body">
                        <div class="d-flex align-items-center justify-content-between">
                            <div>
                                <span class="text-muted text-uppercase fw-semibold font-size-12">Pending Tracks</span>
                                <h3 class="my-1 text-warning fw-bold font-size-22">
                                    {{ formatNumber(summaryKpi.pendingTracks) }}
                                </h3>
                                <span class="text-muted font-size-11">
                                    <i class="bx bx-time-five text-warning me-1"></i> Awaiting action / routing
                                </span>
                            </div>
                            <div class="avatar-sm">
                                <span class="avatar-title bg-soft-warning rounded-circle text-warning font-size-22">
                                    <i class="bx bx-hourglass"></i>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Completed Tracks -->
            <div class="col-xl-3 col-md-6 mb-3">
                <div class="card card-hover-shadow mb-0 h-100 border-start border-4 border-success">
                    <div class="card-body">
                        <div class="d-flex align-items-center justify-content-between">
                            <div>
                                <span class="text-muted text-uppercase fw-semibold font-size-12">Completed Tracks</span>
                                <h3 class="my-1 text-success fw-bold font-size-22">
                                    {{ formatNumber(summaryKpi.completedTracks) }}
                                </h3>
                                <span class="text-muted font-size-11">
                                    <i class="bx bx-check-circle text-success me-1"></i> Successfully finished
                                </span>
                            </div>
                            <div class="avatar-sm">
                                <span class="avatar-title bg-soft-success rounded-circle text-success font-size-22">
                                    <i class="bx bx-check-double"></i>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Completion Rate -->
            <div class="col-xl-3 col-md-6 mb-3">
                <div class="card card-hover-shadow mb-0 h-100 border-start border-4 border-info">
                    <div class="card-body">
                        <div class="d-flex align-items-center justify-content-between">
                            <div class="w-100 me-2">
                                <span class="text-muted text-uppercase fw-semibold font-size-12">Completion Rate</span>
                                <h3 class="my-1 text-info fw-bold font-size-22">
                                    {{ summaryKpi.rate }}%
                                </h3>
                                <div class="progress progress-sm mt-2" style="height: 6px;">
                                    <div class="progress-bar bg-info" role="progressbar"
                                        :style="{ width: summaryKpi.rate + '%' }" :aria-valuenow="summaryKpi.rate"
                                        aria-valuemin="0" aria-valuemax="100"></div>
                                </div>
                            </div>
                            <div class="avatar-sm flex-shrink-0">
                                <span class="avatar-title bg-soft-info rounded-circle text-info font-size-22">
                                    <i class="bx bx-pie-chart-alt"></i>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Main Navigation Card with 2 Tabs -->
        <div class="card">
            <div class="card-header bg-transparent border-bottom p-0">
                <!-- Main Tabs: By Campus / By Delivery Unit using b-tabs -->
                <b-tabs nav-class="nav-tabs-custom" content-class="d-none" v-model="tabIndex">
                    <b-tab :active="activeTab === 'campus'" @click="changeTab('campus')">
                        <template v-slot:title>
                            <span class="py-2 px-1 d-inline-flex align-items-center cursor-pointer">
                                <i class="bx bx-buildings me-2 font-size-18 text-primary"></i>
                                <span class="font-size-15 fw-semibold">By Campus</span>
                            </span>
                        </template>
                    </b-tab>
                    <b-tab :active="activeTab === 'deliveryUnit'" @click="changeTab('deliveryUnit')">
                        <template v-slot:title>
                            <span class="py-2 px-1 d-inline-flex align-items-center cursor-pointer">
                                <i class="bx bx-building me-2 font-size-18 text-info"></i>
                                <span class="font-size-15 fw-semibold">By Delivery Unit</span>
                            </span>
                        </template>
                    </b-tab>
                </b-tabs>
            </div>

            <div class="card-body">
                <!-- Sub-Navigation Bar: ['All', 'By Process Types'] + View Toggles ['Table', 'Bar Graph'] -->
                <div class="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4 rounded">
                    <!-- Left: Sub-buttons ['All', 'By Process Types'] -->
                    <div class="d-flex flex-wrap align-items-center gap-2">
                        <span class="text-muted font-size-12 fw-semibold text-uppercase me-1">Group By:</span>
                        <div class="btn-group shadow-sm" role="group">
                            <button type="button" class="btn btn-sm px-3 font-size-13 fw-medium"
                                :class="subView === 'all' ? 'btn-primary shadow-sm' : 'btn-outline-primary bg-white'"
                                @click="changeSubView('all')">
                                <i class="bx bx-grid-alt me-1 align-middle"></i>
                                All {{ activeTab === 'campus' ? 'Campuses' : 'Delivery Units' }}
                            </button>
                            <button type="button" class="btn btn-sm px-3 font-size-13 fw-medium"
                                :class="subView === 'processTypes' ? 'btn-primary shadow-sm' : 'btn-outline-primary bg-white'"
                                @click="changeSubView('processTypes')">
                                <i class="bx bx-layer me-1 align-middle"></i>
                                By Process Types
                            </button>
                        </div>
                        <div v-if="subView == 'processTypes' || activeTab == 'deliveryUnit'"
                            class="d-flex align-items-center ms-2">
                            <span class="text-muted font-size-12 fw-semibold text-uppercase me-1">{{ GLOBAL_FROM_SCHOOL
                                ? 'Campus' :
                                'Main Office' }}:</span>
                            <b-form-select id="campusSelect" class="form-select" v-model="selectedCampusId" size="sm">
                                <b-form-select-option v-for="campus in campusOptions" :key="campus.id"
                                    :value="campus.id">{{
                                        campus.campusName }}</b-form-select-option>
                            </b-form-select>
                        </div>
                    </div>

                    <!-- Right: View Mode Toggle [Table View / Bar Graph] -->
                    <div class="d-flex align-items-center gap-2">
                        <span class="text-muted font-size-12 fw-semibold text-uppercase me-1">View As:</span>
                        <div class="btn-group shadow-sm" role="group">
                            <button type="button" class="btn btn-sm px-3 font-size-13 fw-medium"
                                :class="displayMode === 'table' ? 'btn-primary' : 'btn-secondary'"
                                @click="displayMode = 'table'">
                                <i class="bx bx-table me-1 align-middle"></i> Table
                            </button>
                            <button type="button" class="btn btn-sm px-3 font-size-13 fw-medium"
                                :class="displayMode === 'chart' ? 'btn-primary' : 'btn-secondary'"
                                @click="displayMode = 'chart'">
                                <i class="bx bx-bar-chart-alt-2 me-1 align-middle"></i> Bar Graph
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Secondary Controls: Search, Dropdown Filter, Pagination Select -->
                <div class="row align-items-center mb-3 g-2">
                    <!-- Search Input -->
                    <div class="col-md-4 col-sm-6">
                        <div class="search-box position-relative">
                            <b-form-input v-model="searchFilter" type="text" class="form-control form-control-sm ps-4"
                                :placeholder="subView === 'all'
                                    ? (activeTab === 'campus' ? 'Search by campus name...' : 'Search by delivery unit...')
                                    : 'Search by process type or name...'"></b-form-input>
                        </div>
                    </div>

                    <!-- Per Page Selector & Clear Search -->
                    <div class="col d-flex justify-content-end align-items-center gap-2">
                        <span class="text-muted font-size-12">Show:</span>
                        <select v-model="perPage" class="form-select form-select-sm w-auto">
                            <option v-for="option in pageOptions" :key="option" :value="option">
                                {{ option }}
                            </option>
                            <option :value="-1">All</option>
                        </select>
                    </div>
                </div>

                <!-- LOADING STATE -->
                <div v-if="loading" class="text-center my-5 py-5">
                    <b-spinner variant="primary" style="width: 3rem; height: 3rem;"
                        label="Loading summary..."></b-spinner>
                    <p class="text-muted mt-3 font-size-14">Fetching document tracking analytics...</p>
                </div>

                <!-- MAIN CONTENT AREA -->
                <div v-else>
                    <!-- 1. TABLE VIEW -->
                    <div v-if="displayMode === 'table'">
                        <div class="table-responsive rounded border">
                            <table class="table table-hover align-middle mb-0 table-striped">
                                <thead class="table-light">
                                    <tr>
                                        <!-- Dynamic Entity Column -->
                                        <th class="cursor-pointer text-nowrap font-size-13" v-if="subView == 'all'"
                                            @click="toggleSort(activeTab === 'campus' ? 'campusName' : 'deliveryUnitName')">
                                            <i class="bx"
                                                :class="activeTab === 'campus' ? 'bx-buildings' : 'bx-building'"></i>
                                            {{ activeTab === 'campus' ? 'Campus Name' : 'Delivery Unit' }}
                                            <i v-if="sortKey === (activeTab === 'campus' ? 'campusName' : 'deliveryUnitName')"
                                                class="bx"
                                                :class="sortDesc ? 'bx-caret-down text-primary' : 'bx-caret-up text-primary'"></i>
                                        </th>

                                        <!-- Process Type Column (When in By Process Types view) -->
                                        <th v-if="subView === 'processTypes'"
                                            class="cursor-pointer text-nowrap font-size-13"
                                            @click="toggleSort('processTypeName')">
                                            <i class="bx bx-task me-1"></i> Process Type
                                            <i v-if="sortKey === 'processTypeName'" class="bx"
                                                :class="sortDesc ? 'bx-caret-down text-primary' : 'bx-caret-up text-primary'"></i>
                                        </th>

                                        <!-- Total Tracks -->
                                        <th class="text-end cursor-pointer text-nowrap font-size-13"
                                            style="width: 150px;" @click="toggleSort('totalTracks')">
                                            Total Tracks
                                            <i v-if="sortKey === 'totalTracks'" class="bx"
                                                :class="sortDesc ? 'bx-caret-down text-primary' : 'bx-caret-up text-primary'"></i>
                                        </th>

                                        <!-- Pending Tracks -->
                                        <th class="text-end cursor-pointer text-nowrap font-size-13"
                                            style="width: 150px;" @click="toggleSort('pendingTracks')">
                                            Pending Tracks
                                            <i v-if="sortKey === 'pendingTracks'" class="bx"
                                                :class="sortDesc ? 'bx-caret-down text-warning' : 'bx-caret-up text-warning'"></i>
                                        </th>

                                        <!-- Completed Tracks -->
                                        <th class="text-end cursor-pointer text-nowrap font-size-13"
                                            style="width: 160px;" @click="toggleSort('completedTracks')">
                                            Completed Tracks
                                            <i v-if="sortKey === 'completedTracks'" class="bx"
                                                :class="sortDesc ? 'bx-caret-down text-success' : 'bx-caret-up text-success'"></i>
                                        </th>

                                        <!-- Completion Rate / Progress -->
                                        <th class="text-center cursor-pointer text-nowrap font-size-13"
                                            style="width: 170px;" @click="toggleSort('completionRate')">
                                            Completion Rate
                                            <i v-if="sortKey === 'completionRate'" class="bx"
                                                :class="sortDesc ? 'bx-caret-down text-info' : 'bx-caret-up text-info'"></i>
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    <!-- EMPTY STATE -->
                                    <tr v-if="filteredList.length === 0">
                                        <td :colspan="subView === 'all' ? 5 : 4" class="text-center py-5">
                                            <div class="my-3">
                                                <i class="bx bx-folder-open font-size-36 text-muted mb-2"></i>
                                                <h5 class="text-muted font-size-15">No records found</h5>
                                                <p class="text-muted font-size-13 mb-0">
                                                    Try adjusting your search keyword, entity filter, or date range.
                                                </p>
                                            </div>
                                        </td>
                                    </tr>

                                    <!-- ROW DATA -->
                                    <tr v-for="(item, idx) in paginatedList" :key="idx">

                                        <!-- Campus Name or Delivery Unit Name -->
                                        <td v-if="subView == 'all'">
                                            <div class="d-flex align-items-center">
                                                <div class="avatar-xs me-2">
                                                    <span class="avatar-title rounded-circle font-size-14"
                                                        :class="activeTab === 'campus' ? 'bg-soft-primary text-primary' : 'bg-soft-info text-info'">
                                                        <i
                                                            :class="activeTab === 'campus' ? 'bx bxs-school' : 'bx bx-building'"></i>
                                                    </span>
                                                </div>
                                                <div>
                                                    <span class="fw-semibold text-dark font-size-13">
                                                        {{ activeTab === 'campus'
                                                            ? (item.campusName || item.campus || 'Campus #' + item.campusId)
                                                            : (item.deliveryUnitName || item.deliveryUnit || 'DU #' +
                                                                item.deliveryUnitId)
                                                        }}
                                                    </span>
                                                    <div v-if="activeTab === 'deliveryUnit' && item.campusName"
                                                        class="text-muted font-size-11">
                                                        <i class="bx bx-map-pin"></i> {{ item.campusName }}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>

                                        <!-- Process Type (in By Process Types view) -->
                                        <td v-if="subView === 'processTypes'">
                                            <span class="font-size-14 px-2 py-1">
                                                <i class="bx bx-file me-1 text-primary"></i>
                                                {{ item.processTypeName || item.processType || 'General Process' }}
                                            </span>
                                        </td>

                                        <!-- Total Tracks -->
                                        <td class="text-end">
                                            <span class="text-primary font-size-15 px-2 py-1 fw-bold">
                                                {{ formatNumber(item.totalTracks) }}
                                            </span>
                                        </td>

                                        <!-- Pending Tracks -->
                                        <td class="text-end">
                                            <span class="text-warning font-size-15 px-2 py-1 fw-bold">
                                                {{ formatNumber(item.pendingTracks) }}
                                            </span>
                                        </td>

                                        <!-- Completed Tracks -->
                                        <td class="text-end">
                                            <span class="text-green font-size-15 px-2 py-1 fw-bold">
                                                {{ formatNumber(item.completedTracks) }}
                                            </span>
                                        </td>

                                        <!-- Completion Rate with Progress Bar -->
                                        <td class="text-center">
                                            <div class="d-flex align-items-center justify-content-center gap-2">
                                                <div class="progress flex-grow-1" style="height: 6px; min-width: 60px;">
                                                    <div class="progress-bar"
                                                        :class="getCompletionPercentage(item.completedTracks, item.totalTracks) >= 50 ? 'bg-success' : 'bg-warning'"
                                                        role="progressbar"
                                                        :style="{ width: getCompletionPercentage(item.completedTracks, item.totalTracks) + '%' }">
                                                    </div>
                                                </div>
                                                <span class="font-size-12 fw-semibold text-muted text-nowrap"
                                                    style="width: 42px;">
                                                    {{ item.totalTracks > 0 ? ((item.completedTracks / item.totalTracks)
                                                        * 100).toFixed(1) : 0 }}%
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- PAGINATION BAR -->
                        <div class="row align-items-center mt-3" v-if="filteredList.length > 0 && perPage !== -1">
                            <div class="col-sm-6 text-muted font-size-12">
                                Showing
                                <span class="fw-semibold text-dark">{{ (currentPage - 1) * perPage + 1 }}</span>
                                to
                                <span class="fw-semibold text-dark">{{ Math.min(currentPage * perPage, totalRows)
                                    }}</span>
                                of
                                <span class="fw-semibold text-dark">{{ totalRows }}</span>
                                entries
                            </div>
                            <div class="col-sm-6">
                                <div class="float-end">
                                    <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage"
                                        pills size="sm" class="pagination-rounded mb-0"></b-pagination>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 2. BAR GRAPH VIEW -->
                    <div v-else class="p-3 border rounded bg-white">
                        <!-- Chart Customization Controls Bar -->
                        <div
                            class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 pb-2 border-bottom">
                            <div>
                                <h6 class="font-size-14 mb-0 text-dark font-weight-bold">
                                    <i class="bx bx-bar-chart-alt-2 text-primary me-1"></i>
                                    Track Distribution Graph &mdash;
                                    {{ activeTab === 'campus' ? 'By Campus' : 'By Delivery Unit' }}
                                    <span v-if="subView === 'processTypes'">({{ entityFilter || 'All Process Types'
                                        }})</span>
                                </h6>
                                <span class="text-muted font-size-11">
                                    Comparing Total, Pending, and Completed tracks across categories
                                </span>
                            </div>

                            <div class="d-flex align-items-center gap-2">
                                <!-- Orientation Toggle -->
                                <button type="button"
                                    class="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                                    @click="toggleOrientation">
                                    <i class="bx"
                                        :class="chartOrientation === 'horizontal' ? 'bx-bar-chart' : 'bx-bar-chart-square'"></i>
                                    <span>{{ chartOrientation === 'horizontal' ? 'Vertical Columns' : 'Horizontal Bars'
                                        }}</span>
                                </button>

                                <!-- Stacked Toggle -->
                                <button type="button" class="btn btn-sm d-flex align-items-center gap-1"
                                    :class="chartStacked ? 'btn-primary' : 'btn-outline-primary'"
                                    @click="toggleStacked">
                                    <i class="bx bx-layer"></i>
                                    <span>{{ chartStacked ? 'Stacked: ON' : 'Grouped Bars' }}</span>
                                </button>
                            </div>
                        </div>

                        <!-- ApexCharts Bar Component -->
                        <div v-if="filteredList.length === 0" class="text-center py-5 text-muted">
                            <i class="bx bx-bar-chart font-size-48 mb-2"></i>
                            <p>No track data available to render graph.</p>
                        </div>
                        <div v-else>
                            <apexchart :key="chartKey" class="apex-charts" type="bar" dir="ltr"
                                :height="chartOptions.chart.height" :series="chartSeries" :options="chartOptions">
                            </apexchart>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}

.date-picker-sm {
    width: 145px;
}

.card-hover-shadow {
    transition: all 0.2s ease-in-out;
}

.card-hover-shadow:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
}

.bg-soft-primary {
    background-color: rgba(85, 110, 230, 0.15) !important;
}

.bg-soft-warning {
    background-color: rgba(241, 180, 76, 0.15) !important;
}

.bg-soft-success {
    background-color: rgba(52, 195, 143, 0.15) !important;
}

.bg-soft-info {
    background-color: rgba(80, 165, 241, 0.15) !important;
}

.bg-soft-secondary {
    background-color: rgba(116, 120, 141, 0.12) !important;
}

.badge-soft-primary {
    background-color: rgba(85, 110, 230, 0.15);
    color: #556ee6;
}

.badge-soft-warning {
    background-color: rgba(241, 180, 76, 0.15);
    color: #f1b44c;
}

.badge-soft-success {
    background-color: rgba(52, 195, 143, 0.15);
    color: #34c38f;
}

.nav-tabs-custom .nav-link {
    border: none;
    border-bottom: 2px solid transparent;
    color: #495057;
}

.nav-tabs-custom .nav-link.active {
    border-bottom: 2px solid #556ee6;
    color: #556ee6 !important;
    background-color: transparent;
}
</style>